import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Spinner from "react-bootstrap/Spinner";
import {
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  User,
} from "firebase/auth";
import { useIonToast } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";

import { auth, googleProvider } from "../../../lib/firebase";
import { useAuth } from "../../../providers/auth";
import Auth from "../../../api/auth";
import GoogleLogo from "../../../theme/images/google-logo.png";

type GoogleAuthButtonProps = {
  type?: "sign-in" | "sign-up";
  loading?: boolean;
  disabled?: boolean;
  onSuccess?: (
    user: User,
    provider?: "password" | "google" | "microsoft",
    autoLogin?: boolean,
  ) => void;
  email?: string;
};

export default function GoogleAuthButton({
  loading = false,
  type = "sign-in",
  disabled = false,
  email = undefined,
  onSuccess,
}: GoogleAuthButtonProps) {
  const [requesting, setRequesting] = React.useState(false);
  const { loading: firebaseLoading } = useAuth();
  const intl = useIntl();
  const [present] = useIonToast();

  const toast = async (msgId: string = "common.tryItLater") => {
    present({
      message: intl.formatMessage({ id: msgId }),
      duration: 5500,
      color: "danger",
      position: "top",
      cssClass: "error-toast",
    });
  };

  const approveOrFail = async (user: User) => {
    if (!user?.email || (email && user.email !== email)) {
      console.log("Email mismatch or missing");
      await auth.currentUser?.delete();
      await toast(email ? "auth.registerUser.error" : undefined);
      return { ok: false, autoLogin: false };
    }

    const { data, err } = await Auth.approve({ email: user.email, type });
    if (err || !data?.ok || !data?.approve) {
      if (data?.autoLogin) {
        return { ok: true, autoLogin: true };
      }
      if (data?.error) {
        const message = data.error[intl.locale] || data.error.en;
        present({
          message,
          duration: 5500,
          color: "danger",
          position: "top",
          cssClass: "error-toast",
        });
      }
      await auth.currentUser?.delete();
      return { ok: false, autoLogin: false };
    }
    return { ok: true, autoLogin: false };
  };

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (requesting || firebaseLoading || disabled) return;

    try {
      setRequesting(true);

      if (Capacitor.isNativePlatform()) {
        const res = await FirebaseAuthentication.signInWithGoogle({
          scopes: ["email"],
          useCredentialManager: false,
        });

        const idToken = res?.credential?.idToken;
        if (!idToken) throw new Error("no_id_token_from_plugin");

        const cred = GoogleAuthProvider.credential(idToken);
        const result = await signInWithCredential(auth, cred);

        const { ok, autoLogin } = await approveOrFail(result.user);
        if (ok && onSuccess) onSuccess(result.user, "google", autoLogin);
        return;
      }

      const result = await signInWithPopup(auth, googleProvider);
      const { ok, autoLogin } = await approveOrFail(result.user);
      if (ok && onSuccess) onSuccess(result.user, "google", autoLogin);
    } catch (e) {
      console.log("auth_signing_error", e);
      await toast();
    } finally {
      setRequesting(false);
    }
  };

  const isSignIn = type === "sign-in";
  const isLoading = requesting || loading;

  return (
    <button
      className="btn btn-visito-outline no-hover w-100 py-8 rounded-2 flex align-items-center justify-content-center gap-2"
      type="button"
      onClick={handleClick}
      disabled={disabled || firebaseLoading || requesting}
    >
      {isLoading ? (
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">
            <FormattedMessage id="common.loading" />
            ...
          </span>
        </Spinner>
      ) : (
        <>
          <img src={GoogleLogo} width={21} alt="Google Authentication" />
          <FormattedMessage
            id={isSignIn ? "auth.login.google" : "auth.register.google"}
          />
        </>
      )}
    </button>
  );
}
